import { useEffect, useRef, useState } from "react";
import { Building2, Tag as TagIcon, Plug, Mail, Phone, Plus, Trash2, Pencil, MessageSquare, FolderOpen, Inbox as InboxIcon, Sparkles, AlertTriangle, CheckCircle2, ExternalLink, Sun, Moon, UsersRound, RefreshCw, Megaphone, Radar } from "lucide-react";
import { Link, useSearchParams } from "react-router-dom";
import { setTheme, useTheme, type Theme } from "@/lib/theme";
import { cn } from "@/lib/utils";
import { TIMEZONES } from "@/lib/timezones";
import { Avatar, Badge, BadgeColorPicker, Button, Combobox, EmptyState, Input, Label, Skeleton, useConfirm, useToast, Select, Modal, ModalFooter } from "@/components/ui";
import { api, ApiError } from "@/lib/api";
import { emailDomainOf, formatSender, sendableDomainsOf, syncsuiteEmailSettingsUrl, useEmailSending } from "@/lib/email-sending";
import { invalidateUsers, useUsers } from "@/lib/use-users";
import type { CustomField, IntegrationsStatus, SenderIdentity, Tag, WorkspaceSettings } from "@/lib/types";
import { PageHeader } from "@/features/command/ui";
import { TrackingTab } from "@/features/tracking/TrackingTab";

type TabKey = "workspace" | "team" | "fields" | "tags" | "tracking" | "integrations";
const TAB_KEYS: TabKey[] = ["workspace", "team", "fields", "tags", "tracking", "integrations"];

export function SettingsPage() {
  // ?tab= keeps the open tab in the address, so Back from a contact opened
  // here (Tracking's send log) lands on the same tab.
  const [params, setParams] = useSearchParams();
  const raw = params.get("tab") as TabKey | null;
  const tab: TabKey = raw && TAB_KEYS.includes(raw) ? raw : "workspace";
  const setTab = (k: TabKey) => setParams(k === "workspace" ? {} : { tab: k }, { replace: true });
  // On a phone the tab strip scrolls sideways: keep the open tab in view.
  const strip = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = strip.current?.querySelector<HTMLElement>('[data-active="true"]');
    if (strip.current && el) strip.current.scrollLeft = Math.max(0, el.offsetLeft - (strip.current.clientWidth - el.clientWidth) / 2);
  }, [tab]);
  return (
    <div className="space-y-4">
      <PageHeader title="Settings" subtitle="Your workspace, team, fields, tags, tracking and connections." />

      <div ref={strip} className="relative flex gap-1 overflow-x-auto border-b border-zinc-800">
        {([
          { k: "workspace",    l: "Workspace",    Icon: Building2 },
          { k: "team",         l: "Team",         Icon: UsersRound },
          { k: "fields",       l: "Custom fields", Icon: TagIcon },
          { k: "tags",         l: "Tags",         Icon: TagIcon },
          { k: "tracking",     l: "Tracking",     Icon: Radar },
          { k: "integrations", l: "Integrations", Icon: Plug },
        ] as { k: TabKey; l: string; Icon: React.ComponentType<{ className?: string }> }[]).map(({ k, l, Icon }) => (
          <button
            key={k}
            type="button"
            data-active={tab === k}
            onClick={() => setTab(k)}
            className={
              "inline-flex shrink-0 items-center gap-2 border-b-2 px-3 py-2 text-sm font-medium transition-colors " +
              (tab === k
                ? "border-indigo-500 text-zinc-100"
                : "border-transparent text-zinc-500 hover:text-zinc-300")
            }
          >
            <Icon className="h-4 w-4" />
            {l}
          </button>
        ))}
      </div>

      {tab === "workspace"    && <WorkspaceTab />}
      {tab === "team"         && <TeamTab />}
      {tab === "fields"       && <FieldsTab />}
      {tab === "tags"         && <TagsTab />}
      {tab === "tracking"     && <TrackingTab />}
      {tab === "integrations" && <IntegrationsTab />}
    </div>
  );
}

// ─── Workspace tab ─────────────────────────────────────────────────────────
function WorkspaceTab() {
  const toast = useToast();
  const [s, setS] = useState<WorkspaceSettings | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get<{ settings: WorkspaceSettings }>("/settings")
      .then((d) => setS(d.settings))
      .catch((err) => toast.error(err instanceof ApiError ? err.message : "Failed"));
  }, [toast]);

  const save = async () => {
    if (!s) return;
    setSaving(true);
    try {
      const res = await api.patch<{ settings: WorkspaceSettings }>("/settings", {
        workspace_name: s.workspace_name,
        timezone: s.timezone,
        default_from_email: s.default_from_email,
        default_from_name: s.default_from_name,
        campaign_from_email: s.campaign_from_email,
        campaign_from_name: s.campaign_from_name,
        default_phone: s.default_phone,
      });
      setS(res.settings);
      toast.success("Saved");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally { setSaving(false); }
  };

  if (!s) return <Skeleton className="h-64" />;

  return (
    <div className="grid grid-cols-1 gap-4 lg:max-w-3xl">
      <Section title="Branding">
        <div><Label>Workspace name</Label><Input value={s.workspace_name} onChange={(e) => setS({ ...s, workspace_name: e.target.value })} /></div>
        <div className="mt-3">
          <Label>Timezone</Label>
          <Combobox
            value={s.timezone}
            onChange={(v) => setS({ ...s, timezone: v })}
            options={(TIMEZONES.includes(s.timezone)
              ? TIMEZONES
              : [s.timezone, ...TIMEZONES]
            ).map((tz) => ({ value: tz }))}
            placeholder="America/Los_Angeles"
          />
        </div>
      </Section>
      <AppearanceSection />
      <EmailSenderSection
        fromEmail={s.default_from_email}
        fromName={s.default_from_name}
        campaignFromEmail={s.campaign_from_email ?? null}
        campaignFromName={s.campaign_from_name ?? null}
        onChange={(patch) => setS({ ...s, ...patch })}
        savedAt={s.updated_at}
      />
      <Section title="Default phone">
        <div className="sm:max-w-xs">
          <Label>Outbound SMS / calls</Label>
          <Input type="tel" leftIcon={<Phone className="h-3.5 w-3.5" />} value={s.default_phone ?? ""} onChange={(e) => setS({ ...s, default_phone: e.target.value || null })} placeholder="+1…" />
        </div>
      </Section>
      <div className="flex justify-end">
        <Button onClick={save} loading={saving}>Save changes</Button>
      </div>
    </div>
  );
}

/**
 * Email sender setup.
 *
 * The rule this panel exists to make obvious: SyncSuite only verifies the
 * DOMAIN of your sending address. Any mailbox on a verified domain works —
 * and your SyncSuite login email is irrelevant unless it happens to be on
 * one. Leave the field blank and sends use the workspace default.
 *
 * Verified domains are listed as one-click fills, and an address on an
 * unverified domain is flagged before the user finds out via a bounce.
 */
function EmailSenderSection({
  fromEmail,
  fromName,
  campaignFromEmail,
  campaignFromName,
  onChange,
  savedAt,
}: {
  fromEmail: string | null;
  fromName: string | null;
  campaignFromEmail: string | null;
  campaignFromName: string | null;
  onChange: (patch: {
    default_from_email?: string | null; default_from_name?: string | null;
    campaign_from_email?: string | null; campaign_from_name?: string | null;
  }) => void;
  /** Changes on save — our cue to re-read the resolved effective sender. */
  savedAt: string;
}) {
  const { config, loading, refresh } = useEmailSending();
  const settingsUrl = syncsuiteEmailSettingsUrl();

  // Re-resolve after a save so "Emails will send as" reflects the value
  // just written rather than the one loaded on mount.
  useEffect(() => { void refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [savedAt]);

  const typedDomain = emailDomainOf(fromEmail);
  const typedCampaignDomain = emailDomainOf(campaignFromEmail);
  const verified = config?.known ? config.verified_domains : [];
  // Verified hostnames plus the parent of each verified subdomain — what an
  // address may actually use. Only claim a domain is unverified when we
  // actually know the list.
  const sendable = sendableDomainsOf(config);
  const domainOk = !config?.known || !typedDomain || sendable.includes(typedDomain);
  const campaignDomainOk = !config?.known || !typedCampaignDomain || sendable.includes(typedCampaignDomain);

  return (
    <Section title="Email sender">
      <p className="text-xs text-zinc-400">
        Only the <span className="text-zinc-200">domain</span> has to be verified: any mailbox on it works
        (<span className="text-zinc-300">hello@</span>, <span className="text-zinc-300">news@</span>, …). This is
        independent of the email you log in with. Leave blank to use the workspace default. Each team member can
        have their own address under the <span className="text-zinc-300">Team</span> tab.
      </p>

      {loading ? (
        <Skeleton className="mt-3 h-16" />
      ) : !config || !config.known ? null : verified.length === 0 ? (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <div>
            <p className="font-medium">No verified sending domain, so outbound email can't send yet.</p>
            <p className="mt-1 text-amber-200/80">
              Add your domain in SyncSuite, set the DNS records it gives you, and it'll appear here.
              {config.pending_domains.length > 0 && (
                <> Waiting on verification: {config.pending_domains.map((d) => d.hostname).join(", ")}.</>
              )}
            </p>
            {settingsUrl && (
              <a href={settingsUrl} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 font-medium underline hover:text-amber-100">
                Open Email settings in SyncSuite <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        </div>
      ) : (
        <div className="mt-3 rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
          <p className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">You can send from</p>
          <div className="mt-1.5 flex flex-wrap gap-1.5">
            {sendable.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => onChange({ default_from_email: `${(fromEmail?.split("@")[0] || "info")}@${d}` })}
                title={`Use an address on ${d}`}
                className="inline-flex items-center gap-1 rounded-md bg-emerald-500/10 px-2 py-1 text-xs font-medium text-emerald-300 hover:bg-emerald-500/20"
              >
                <CheckCircle2 className="h-3 w-3" /> {d}
              </button>
            ))}
          </div>
          {config.effective.from && (
            <p className="mt-2.5 text-xs text-zinc-400">
              Your emails will send as{" "}
              <span className="text-zinc-100">{formatSender(config.effective.from_name, config.effective.from)}</span>
              {config.effective.reply_to && <> · replies go to <span className="text-zinc-300">{config.effective.reply_to}</span></>}
            </p>
          )}
          {config.campaign?.from && (
            <p className="mt-1 text-xs text-zinc-400">
              Newsletters &amp; campaigns will send as{" "}
              <span className="text-zinc-100">{formatSender(config.campaign.from_name, config.campaign.from)}</span>
            </p>
          )}
        </div>
      )}

      {/* A verified domain isn't enough on its own: SyncSuite refuses every
          send — personal and marketing alike — until the workspace's
          mailing address is on file. Personal email never displays it, so
          without this warning the only symptom is a failing send. */}
      {config?.known && !config.address_on_file && (
        <div className="mt-3 flex items-start gap-2 rounded-lg border border-amber-500/30 bg-amber-500/10 p-3 text-xs text-amber-200">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <div>
            <p className="font-medium">No business mailing address, so outbound email can't send yet.</p>
            <p className="mt-1 text-amber-200/80">
              SyncSuite needs your business mailing address before any email goes out. It's never shown in
              personal email: US law only requires it in the footer of marketing email, where it's added
              automatically.
            </p>
            {settingsUrl && (
              <a href={settingsUrl} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 font-medium underline hover:text-amber-100">
                Add it in SyncSuite <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        </div>
      )}

      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <Label>From email</Label>
          <Input
            type="email"
            leftIcon={<Mail className="h-3.5 w-3.5" />}
            value={fromEmail ?? ""}
            onChange={(e) => onChange({ default_from_email: e.target.value || null })}
            placeholder={config?.platform_default_sender ?? "hello@yourdomain.com"}
          />
          {!domainOk && (
            <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-400">
              <AlertTriangle className="h-3 w-3" /> {typedDomain} isn't verified, so sends from it will fail.
            </p>
          )}
        </div>
        <div>
          <Label>From name</Label>
          <Input
            value={fromName ?? ""}
            onChange={(e) => onChange({ default_from_name: e.target.value || null })}
            placeholder="Your business name"
          />
        </div>
      </div>

      {/* Newsletters usually come from the brand ("200 Lights <hello@…>")
          while everyday mail comes from a person ("Kenny | 200 Lights").
          Blank = same as the default above. */}
      <div className="mt-4 border-t border-zinc-800/60 pt-4">
        <p className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-300">
          <Megaphone className="h-3.5 w-3.5 text-zinc-500" /> Newsletters &amp; campaigns
        </p>
        <p className="mt-1 text-xs text-zinc-500">Leave blank to use the sender above.</p>
        <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <Label>From email</Label>
            <Input
              type="email"
              leftIcon={<Mail className="h-3.5 w-3.5" />}
              value={campaignFromEmail ?? ""}
              onChange={(e) => onChange({ campaign_from_email: e.target.value || null })}
              placeholder={fromEmail || config?.platform_default_sender || "hello@yourdomain.com"}
            />
            {!campaignDomainOk && (
              <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-400">
                <AlertTriangle className="h-3 w-3" /> {typedCampaignDomain} isn't verified, so sends from it will fail.
              </p>
            )}
          </div>
          <div>
            <Label>From name</Label>
            <Input
              value={campaignFromName ?? ""}
              onChange={(e) => onChange({ campaign_from_name: e.target.value || null })}
              placeholder={fromName || "Your business name"}
            />
          </div>
        </div>
      </div>
    </Section>
  );
}

// ─── Team tab ───────────────────────────────────────────────────────────────
/**
 * Who is on the team, and what address each person sends email as.
 *
 * Membership comes from the SyncSuite workspace (Members page); the backend
 * creates a CRM login for every verified member, so nobody has to "open the
 * CRM once" before work can be assigned to them. The per-person sender maps
 * a login (often a personal gmail) to the business address they write from.
 */
function TeamTab() {
  const toast = useToast();
  const confirm = useConfirm();
  const { users, loading, refresh } = useUsers();
  const { config } = useEmailSending();
  const [identities, setIdentities] = useState<SenderIdentity[] | null>(null);
  const [syncing, setSyncing] = useState(false);
  const sendable = sendableDomainsOf(config);

  const loadIdentities = async () => {
    try {
      const d = await api.get<{ identities: SenderIdentity[] }>("/settings/sender-identities");
      setIdentities(d.identities);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
      setIdentities([]);
    }
  };
  useEffect(() => { void loadIdentities(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post<{ sync: { ok: boolean; created: { email: string }[]; error?: string } }>("/settings/users/sync");
      invalidateUsers();
      await refresh(true);
      if (!r.sync.ok) toast.error(r.sync.error ? `Couldn't check the workspace: ${r.sync.error}` : "Couldn't check the workspace members");
      else if (r.sync.created.length > 0) toast.success(`Added ${r.sync.created.map((c) => c.email).join(", ")}`);
      else toast.success("Everyone in the workspace already has a CRM login");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally { setSyncing(false); }
  };

  const save = async (loginEmail: string, fromEmail: string, fromName: string) => {
    const r = await api.put<{ identity: SenderIdentity }>("/settings/sender-identities", {
      login_email: loginEmail, from_email: fromEmail, from_name: fromName || null,
    });
    setIdentities((prev) => {
      const rest = (prev ?? []).filter((i) => i.login_email !== r.identity.login_email);
      return [...rest, r.identity].sort((a, b) => a.login_email.localeCompare(b.login_email));
    });
    toast.success("Saved");
  };

  const remove = async (loginEmail: string) => {
    if (!(await confirm({ title: "Send as the workspace default instead?", confirmLabel: "Remove" }))) return;
    // A refusal (only an owner or admin may remove someone else's) says why;
    // the click used to be fire-and-forget, so a 403 was silent.
    try {
      await api.delete(`/settings/sender-identities/${encodeURIComponent(loginEmail)}`);
      setIdentities((prev) => (prev ?? []).filter((i) => i.login_email !== loginEmail));
      toast.success("Removed");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    }
  };

  return (
    <div className="grid grid-cols-1 gap-4 lg:max-w-3xl">
      <Section title="Team members">
        <div className="flex items-start justify-between gap-3">
          <p className="text-xs leading-relaxed text-zinc-400">
            Everyone invited to the workspace in SyncSuite gets a login here automatically. Tasks, conversations
            and appointments can be assigned to any of them; see each person's work on the{" "}
            <Link to="/team" className="text-indigo-300 hover:underline">Team page</Link>.
          </p>
          <Button variant="ghost" size="sm" leftIcon={<RefreshCw className={cn("h-3.5 w-3.5", syncing && "animate-spin")} />} onClick={sync} loading={syncing}>
            Sync
          </Button>
        </div>
        {loading && users.length === 0 ? (
          <Skeleton className="mt-3 h-24" />
        ) : users.length === 0 ? (
          <p className="mt-3 text-xs text-zinc-500">Nobody yet. Invite people on the workspace Members page, then Sync.</p>
        ) : (
          <ul className="mt-3 divide-y divide-zinc-800/50 rounded-lg border border-zinc-800/70">
            {users.map((u) => (
              <TeamMemberRow
                key={u.id}
                user={u}
                identity={identities?.find((i) => i.login_email.toLowerCase() === u.email.toLowerCase()) ?? null}
                sendable={sendable}
                fallback={config?.known ? (config.configured_from_email || config.platform_default_sender) : null}
                onSave={(fromEmail, fromName) => save(u.email, fromEmail, fromName)}
                onRemove={() => remove(u.email)}
              />
            ))}
          </ul>
        )}
      </Section>
    </div>
  );
}

function TeamMemberRow({ user, identity, sendable, fallback, onSave, onRemove }: {
  user: { id: string; name: string; email: string; image: string | null; role: string | null };
  identity: SenderIdentity | null;
  sendable: string[];
  /** Address used when this person has no identity of their own. */
  fallback: string | null;
  onSave: (fromEmail: string, fromName: string) => Promise<void>;
  onRemove: () => Promise<void>;
}) {
  const toast = useToast();
  const [editing, setEditing] = useState(false);
  const [fromEmail, setFromEmail] = useState(identity?.from_email ?? "");
  const [fromName, setFromName] = useState(identity?.from_name ?? "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setFromEmail(identity?.from_email ?? ""); setFromName(identity?.from_name ?? ""); }, [identity]);

  const typedDomain = emailDomainOf(fromEmail);
  const domainOk = sendable.length === 0 || !typedDomain || sendable.includes(typedDomain);

  const submit = async () => {
    if (!fromEmail.trim()) return;
    setSaving(true);
    try { await onSave(fromEmail.trim(), fromName.trim()); setEditing(false); }
    catch (err) { toast.error(err instanceof ApiError ? err.message : "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <li className="px-3 py-3">
      <div className="flex items-center gap-3">
        <Avatar name={user.name} email={user.email} src={user.image} size="sm" />
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-zinc-100">{user.name || user.email}</p>
          <p className="truncate text-xs text-zinc-500">{user.email}</p>
        </div>
        <div className="min-w-0 text-right">
          {identity ? (
            <>
              <p className="truncate text-xs text-zinc-200">Sends as {formatSender(identity.from_name, identity.from_email)}</p>
              {identity.domain_verified === false && (
                <p className="inline-flex items-center gap-1 text-[11px] text-amber-400"><AlertTriangle className="h-3 w-3" /> domain not verified</p>
              )}
            </>
          ) : (
            <p className="truncate text-xs text-zinc-500">{fallback ? `Sends as ${user.name} <${fallback}>` : "Sends as the workspace default"}</p>
          )}
        </div>
        <Button variant="ghost" size="sm" onClick={() => setEditing((v) => !v)} leftIcon={<Pencil className="h-3.5 w-3.5" />}>
          <span className="hidden sm:inline">{identity ? "Change" : "Set address"}</span>
        </Button>
      </div>
      {editing && (
        <div className="mt-3 grid grid-cols-1 gap-3 rounded-lg border border-zinc-800/70 bg-zinc-900/40 p-3 sm:grid-cols-2">
          <div>
            <Label>Sends from</Label>
            <Input type="email" leftIcon={<Mail className="h-3.5 w-3.5" />} value={fromEmail} onChange={(e) => setFromEmail(e.target.value)} placeholder={`${user.name.split(" ")[0]?.toLowerCase() || "name"}@${sendable[sendable.length - 1] ?? "yourdomain.com"}`} />
            {!domainOk && (
              <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-amber-400">
                <AlertTriangle className="h-3 w-3" /> {typedDomain} isn't verified, so sends from it will fail.
              </p>
            )}
          </div>
          <div>
            <Label>Shown as</Label>
            <Input value={fromName} onChange={(e) => setFromName(e.target.value)} placeholder={`${user.name} | Your business`} />
          </div>
          <div className="flex items-center gap-2 sm:col-span-2">
            <Button size="sm" onClick={submit} loading={saving} disabled={!fromEmail.trim()}>Save</Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>Cancel</Button>
            <div className="flex-1" />
            {identity && (
              <Button size="sm" variant="ghost" leftIcon={<Trash2 className="h-3.5 w-3.5" />} onClick={() => void onRemove()}>Remove</Button>
            )}
          </div>
        </div>
      )}
    </li>
  );
}

/** Dark/light theme choice. Saved per browser (not on the workspace) — it
 *  takes effect immediately, no Save button needed. Light is the default. */
function AppearanceSection() {
  const theme = useTheme();
  const options: { value: Theme; label: string; Icon: typeof Sun; blurb: string }[] = [
    { value: "dark",  label: "Dark",  Icon: Moon, blurb: "Easy on the eyes" },
    { value: "light", label: "Light", Icon: Sun,  blurb: "Bright and crisp (default)" },
  ];
  return (
    <Section title="Appearance">
      <p className="mb-3 text-xs leading-relaxed text-zinc-400">
        Choose how the CRM looks on this device. Changes apply right away.
      </p>
      <div className="grid grid-cols-2 gap-2 sm:max-w-md">
        {options.map(({ value, label, Icon, blurb }) => {
          const active = theme === value;
          return (
            <button
              key={value}
              type="button"
              onClick={() => setTheme(value)}
              aria-pressed={active}
              className={cn(
                "flex items-center gap-3 rounded-xl border px-3 py-2.5 text-left transition-colors",
                active
                  ? "border-indigo-500 bg-indigo-500/10"
                  : "border-zinc-800 bg-zinc-900/40 hover:border-zinc-700 hover:bg-zinc-900"
              )}
            >
              <Icon className={cn("h-4 w-4 shrink-0", active ? "text-indigo-400" : "text-zinc-500")} />
              <span className="min-w-0">
                <span className={cn("block text-sm font-medium", active ? "text-zinc-100" : "text-zinc-300")}>{label}</span>
                <span className="block truncate text-[11px] text-zinc-500">{blurb}</span>
              </span>
            </button>
          );
        })}
      </div>
    </Section>
  );
}


// ─── Custom fields tab ─────────────────────────────────────────────────────
function FieldsTab() {
  const toast = useToast();
  const confirm = useConfirm();
  const [fields, setFields] = useState<CustomField[]>([]);
  const [loading, setLoading] = useState(true);
  // null = closed, "new" = create form, CustomField = edit form. Keeps the
  // dialog wired to a single state instead of separate open + target flags.
  const [editing, setEditing] = useState<CustomField | "new" | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const d = await api.get<{ fields: CustomField[] }>("/custom-fields");
      setFields(d.fields);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, []);

  const onDelete = async (f: CustomField) => {
    if (!(await confirm({
      title: `Delete field "${f.label}"?`,
      description: "Existing data on contacts is kept. Only the field definition is removed.",
      confirmLabel: "Delete field",
      danger: true,
    }))) return;
    try {
      await api.delete(`/custom-fields/${f.id}`);
      toast.success("Field deleted");
      load();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    }
  };

  return (
    <div className="lg:max-w-3xl space-y-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-zinc-500 max-w-prose">Add custom data fields to your contacts. Ask AI to create them, or do it manually.</p>
        <Button leftIcon={<Plus className="h-4 w-4" />} size="sm" onClick={() => setEditing("new")} className="shrink-0">New field</Button>
      </div>
      {loading ? <Skeleton className="h-40" /> : fields.length === 0 ? (
        <EmptyState icon={TagIcon} title="No custom fields" description="Custom fields show up on contact records and can be used in lists, automations, and reports." />
      ) : (
        <ul className="overflow-hidden rounded-xl border border-zinc-800/70 bg-zinc-900/30">
          {fields.map((f) => (
            <li key={f.id} className="flex items-start justify-between gap-3 border-b border-zinc-800/40 px-4 py-3 last:border-0">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-zinc-100 truncate">{f.label}</p>
                <p className="text-xs text-zinc-500">
                  {FIELD_TYPE_LABELS[f.field_type] ?? f.field_type}
                  {f.required && " · required"}
                  {f.options.length > 0 && ` · ${f.options.length} options`}
                </p>
                {f.context && <p className="mt-1 text-xs text-zinc-400 italic">{f.context}</p>}
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button
                  onClick={() => setEditing(f)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
                  aria-label="Edit"
                  title="Edit field"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => onDelete(f)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded text-zinc-400 hover:bg-rose-500/10 hover:text-rose-400"
                  aria-label="Delete"
                  title="Delete field"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <FieldDialog
        target={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); load(); }}
      />
    </div>
  );
}

// Field-type list with friendly labels + descriptions. Order matters — common
// types first, niche ones last.
const FIELD_TYPE_OPTIONS: { value: CustomField["field_type"]; label: string; hint: string }[] = [
  { value: "text",        label: "Short text",         hint: "A short answer (one line)" },
  { value: "longtext",    label: "Long text",          hint: "A multi-line note or paragraph" },
  { value: "number",      label: "Number",             hint: "Whole or decimal numbers" },
  { value: "boolean",     label: "Yes / No",           hint: "A simple checkbox: yes or no" },
  { value: "dropdown",    label: "Dropdown",           hint: "Pick one from a list of options" },
  { value: "multiselect", label: "Multi-select",       hint: "Pick more than one from a list" },
  { value: "date",        label: "Date",               hint: "A specific calendar date" },
  { value: "datetime",    label: "Date & time",        hint: "A date with a specific time" },
  { value: "email",       label: "Email address",      hint: "Validates as an email" },
  { value: "phone",       label: "Phone number",       hint: "A phone number" },
  { value: "url",         label: "Website / URL",      hint: "A web link" },
];

/**
 * Unified create + edit dialog. `target` controls mode: "new" creates a
 * fresh field (with an auto-generated `key`), a CustomField instance puts
 * the dialog in edit mode. The auto-generated key is set on creation only
 * — automations and saved data continue to reference it after the user
 * renames the label, so the relationships stay intact.
 */
function FieldDialog({ target, onClose, onSaved }: {
  target: CustomField | "new" | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const toast = useToast();
  const open = target !== null;
  const editing = target && target !== "new" ? target : null;
  const [label, setLabel] = useState("");
  const [type, setType] = useState<CustomField["field_type"]>("text");
  const [optionsText, setOptionsText] = useState("");
  const [context, setContext] = useState("");
  const [saving, setSaving] = useState(false);

  // Reset every time the dialog opens for a different target.
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setLabel(editing.label);
      setType(editing.field_type);
      setOptionsText(editing.options.join("\n"));
      setContext(editing.context ?? "");
    } else {
      setLabel(""); setType("text"); setOptionsText(""); setContext("");
    }
  }, [open, editing]);

  // The internal key is derived from the label only on create. Renaming
  // the label later does NOT change the key — that's what keeps existing
  // contact data + automation references stable.
  const key = label.toLowerCase().trim().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
  const typeOption = FIELD_TYPE_OPTIONS.find((t) => t.value === type)!;
  const contextOk = context.trim().length >= 10;

  const submit = async () => {
    if (!label.trim() || !contextOk) return;
    if (!editing && !key) return;
    setSaving(true);
    try {
      const body = {
        label: label.trim(),
        field_type: type,
        context: context.trim(),
        options: (type === "dropdown" || type === "multiselect")
          ? optionsText.split("\n").map((s) => s.trim()).filter(Boolean)
          : undefined,
      };
      if (editing) {
        await api.patch(`/custom-fields/${editing.id}`, body);
        toast.success("Field updated");
      } else {
        await api.post("/custom-fields", { key, ...body });
        toast.success("Field created");
      }
      onSaved();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={editing ? "Edit custom field" : "Add a custom field"} size="md">
      <div className="space-y-4">
        <div>
          <Label required>What do you want to call this field?</Label>
          <Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Annual Revenue, Birthday, Account Manager" autoFocus />
          <p className="mt-1.5 text-xs text-zinc-500">
            This is what you and your team will see on every contact.
            {editing && <> Renaming is safe: the field's underlying ID stays the same, so existing data and automations keep working.</>}
          </p>
        </div>

        <div>
          <Label required>What kind of information is this?</Label>
          <Select value={type} onChange={(e) => setType(e.target.value as CustomField["field_type"])}>
            {FIELD_TYPE_OPTIONS.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </Select>
          <p className="mt-1.5 text-xs text-zinc-500">{typeOption.hint}</p>
        </div>

        {(type === "dropdown" || type === "multiselect") && (
          <div>
            <Label required>What are the choices? (one per line)</Label>
            <textarea
              value={optionsText}
              onChange={(e) => setOptionsText(e.target.value)}
              rows={5}
              className="w-full rounded-lg border border-zinc-800 bg-zinc-900/50 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-indigo-500/50 focus:outline-none"
              placeholder={"Small\nMedium\nLarge"}
            />
          </div>
        )}

        <div>
          <Label required>What is this field for?</Label>
          <textarea
            value={context}
            onChange={(e) => setContext(e.target.value)}
            rows={3}
            className="w-full rounded-lg border border-zinc-800 bg-zinc-900/50 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-indigo-500/50 focus:outline-none"
            placeholder="e.g. The customer's annual revenue. We use this to qualify them for our enterprise pricing tier."
          />
          <p className="mt-1.5 text-xs text-zinc-500">
            Helps the AI know when to read or write this field in automations and message templates.
            {context.length > 0 && context.length < 10 && (
              <span className="ml-1 text-amber-400">(at least 10 characters)</span>
            )}
          </p>
        </div>
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} loading={saving} disabled={!label.trim() || !contextOk || (!editing && !key)}>
          {editing ? "Save changes" : "Add field"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

const FIELD_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  FIELD_TYPE_OPTIONS.map((o) => [o.value, o.label])
);

// ─── Tags tab ──────────────────────────────────────────────────────────────
function TagsTab() {
  const toast = useToast();
  const confirm = useConfirm();
  const [tags, setTags] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  // null = closed, "new" = create form, Tag = edit form.
  const [editing, setEditing] = useState<Tag | "new" | null>(null);

  const load = async () => {
    setLoading(true);
    try {
      const d = await api.get<{ tags: Tag[] }>("/tags");
      setTags(d.tags);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { void load(); }, []);

  const onDelete = async (t: Tag) => {
    const usageCount = t.usage_count ?? 0;
    if (!(await confirm({
      title: `Delete tag "${t.name}"?`,
      description: usageCount > 0
        ? `This will remove the tag from ${usageCount} contact${usageCount === 1 ? "" : "s"}.`
        : "This tag isn't currently in use.",
      confirmLabel: "Delete tag",
      danger: true,
    }))) return;
    const before = tags;
    setTags((prev) => prev.filter((x) => x.id !== t.id));
    try {
      await api.delete(`/tags/${t.id}`);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
      setTags(before);
    }
  };

  return (
    <div className="lg:max-w-3xl space-y-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-zinc-500 max-w-prose">Tags help organize contacts and trigger automations. Each tag includes context the AI reads to know when to apply or remove it.</p>
        <Button leftIcon={<Plus className="h-4 w-4" />} size="sm" onClick={() => setEditing("new")} className="shrink-0">New tag</Button>
      </div>
      {loading ? <Skeleton className="h-40" /> : tags.length === 0 ? (
        <EmptyState icon={TagIcon} title="No tags yet" description="Tags help organize contacts and trigger automations." action={<Button leftIcon={<Plus className="h-4 w-4" />} onClick={() => setEditing("new")}>Create your first tag</Button>} />
      ) : (
        <ul className="overflow-hidden rounded-xl border border-zinc-800/70 bg-zinc-900/30">
          {tags.map((t) => (
            <li key={t.id} className="flex items-start justify-between gap-3 border-b border-zinc-800/40 px-4 py-3 last:border-0">
              <div className="flex flex-1 items-start gap-3 min-w-0">
                {/* Fixed-width badge column keeps all descriptions aligned
                    to the same left edge regardless of the tag's name length. */}
                <div className="w-32 shrink-0 pt-0.5">
                  <Badge color={t.color}>{t.name}</Badge>
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-zinc-500">{t.usage_count ?? 0} contacts</p>
                  {t.context && <p className="mt-1 text-xs text-zinc-400 italic">{t.context}</p>}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button
                  onClick={() => setEditing(t)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded text-zinc-400 hover:bg-zinc-800 hover:text-zinc-100"
                  aria-label="Edit"
                  title="Edit tag"
                >
                  <Pencil className="h-3.5 w-3.5" />
                </button>
                <button
                  onClick={() => onDelete(t)}
                  className="inline-flex h-8 w-8 items-center justify-center rounded text-zinc-400 hover:bg-rose-500/10 hover:text-rose-400"
                  aria-label="Delete"
                  title="Delete tag"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <TagDialog
        target={editing}
        onClose={() => setEditing(null)}
        onSaved={(t) => {
          setEditing(null);
          // Optimistic local merge for create; for edit we just trust the
          // returned row to be canonical. Either way reload so usage_count
          // and any backend-side changes show up.
          if (t) setTags((prev) => {
            const idx = prev.findIndex((x) => x.id === t.id);
            if (idx === -1) return [...prev, { ...t, usage_count: 0 }];
            const next = [...prev]; next[idx] = { ...t, usage_count: prev[idx]!.usage_count }; return next;
          });
          load();
        }}
      />
    </div>
  );
}

/**
 * Unified create + edit dialog for tags. Editing the name only changes
 * the display label — contact-tag relationships are stored by tag ID, so
 * existing tagged contacts and automations referencing the tag's row keep
 * working. (Smart-list rules that reference the tag by name will need to
 * be updated after a rename, since those store the literal name.)
 */
function TagDialog({ target, onClose, onSaved }: {
  target: Tag | "new" | null;
  onClose: () => void;
  onSaved: (t: Tag | null) => void;
}) {
  const toast = useToast();
  const open = target !== null;
  const editing = target && target !== "new" ? target : null;
  const [name, setName] = useState("");
  const [color, setColor] = useState("indigo");
  const [context, setContext] = useState("");
  const [saving, setSaving] = useState(false);
  const contextOk = context.trim().length >= 10;

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setName(editing.name);
      setColor(editing.color);
      setContext(editing.context ?? "");
    } else {
      setName(""); setColor("indigo"); setContext("");
    }
  }, [open, editing]);

  const submit = async () => {
    if (!name.trim() || !contextOk) return;
    setSaving(true);
    try {
      if (editing) {
        const r = await api.patch<{ tag: Tag }>(`/tags/${editing.id}`, {
          name: name.trim(), color, context: context.trim(),
        });
        toast.success("Tag updated");
        onSaved(r.tag);
      } else {
        const r = await api.post<{ tag: Tag }>("/tags", {
          name: name.trim(), color, context: context.trim(),
        });
        toast.success("Tag created");
        onSaved(r.tag);
      }
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={editing ? "Edit tag" : "Add a tag"} size="md">
      <div className="space-y-4">
        <div>
          <Label required>Tag name</Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. VIP, Hot Lead, Customer" autoFocus />
          {editing && (
            <p className="mt-1.5 text-xs text-zinc-500">
              Renaming the tag is safe: your tagged contacts and automation triggers stay linked by tag ID.
            </p>
          )}
        </div>
        <div>
          <Label>Color</Label>
          <BadgeColorPicker value={color} onChange={setColor} />
        </div>
        <div>
          <Label required>When should this tag be used?</Label>
          <textarea
            value={context}
            onChange={(e) => setContext(e.target.value)}
            rows={3}
            className="w-full rounded-lg border border-zinc-800 bg-zinc-900/50 px-3 py-2 text-sm text-zinc-100 placeholder:text-zinc-500 focus:border-indigo-500/50 focus:outline-none"
            placeholder="e.g. Apply when a lead has replied within 24 hours and asked about pricing. Remove when they convert to a customer or go cold for 30+ days."
          />
          <p className="mt-1.5 text-xs text-zinc-500">
            Helps the AI know when to apply or remove this tag in automations and smart lists.
            {context.length > 0 && context.length < 10 && (
              <span className="ml-1 text-amber-400">(at least 10 characters)</span>
            )}
          </p>
        </div>
      </div>
      <ModalFooter>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button onClick={submit} loading={saving} disabled={!name.trim() || !contextOk}>
          {editing ? "Save changes" : "Add tag"}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

/**
 * Plain-English status panel. The underlying integrations are technical
 * (Postgres / Redis / API keys / webhook secrets) but the user shouldn't
 * need to know any of that — they just need to know whether things work
 * and what to do if they don't. We bucket the raw flags into four
 * everyday capabilities and only escalate the technical detail when
 * something's actually broken.
 */
function IntegrationsTab() {
  const toast = useToast();
  const [status, setStatus] = useState<IntegrationsStatus | null>(null);

  useEffect(() => {
    api.get<{ integrations: IntegrationsStatus }>("/settings")
      .then((d) => setStatus(d.integrations))
      .catch((err) => toast.error(err instanceof ApiError ? err.message : "Failed"));
  }, [toast]);

  if (!status) return <Skeleton className="h-64" />;

  // Plain-English buckets the user actually cares about. Each one rolls
  // up the technical flags it depends on so we can show one row instead
  // of five. Order matters — the foundational pieces (data + background
  // automations) come first so the user reads them before the
  // messaging-specific rows.
  const items: { title: string; explainer: string; ok: boolean; Icon: React.ComponentType<{ className?: string }>; details?: string }[] = [
    {
      title: "Saving your data",
      explainer: "Stores your contacts, messages, tasks, notes, and everything else the CRM keeps track of.",
      ok: status.database,
      Icon: FolderOpen,
      details: status.database ? undefined : "The CRM can't reach its data storage right now. Ask your AI to check it.",
    },
    {
      title: "Background automations",
      explainer: "Runs automations, bulk imports, and scheduled tasks so things happen even when you're not looking at the CRM.",
      ok: status.redis,
      Icon: Sparkles,
      details: status.redis ? undefined : "Background automations aren't running, so automations and bulk actions might be slower or skipped.",
    },
    {
      title: "Sending messages",
      explainer: "Lets the CRM send emails, text messages, and social DMs through your SyncSuite account.",
      ok: status.services_api,
      Icon: MessageSquare,
      details: status.services_api ? undefined : "SyncSuite isn't connected yet. Without it, outbound email, SMS, and social won't go anywhere. Ask your AI to link it.",
    },
    {
      title: "Receiving messages",
      explainer: "Catches incoming texts, emails, and social DMs and drops them into your inbox.",
      ok: status.inbound_webhook_secret && status.mothership_url,
      Icon: InboxIcon,
      details: (status.inbound_webhook_secret && status.mothership_url)
        ? undefined
        : "Inbound delivery isn't fully wired up. New messages from your customers may not show up until this is fixed.",
    },
  ];

  const allGood = items.every((i) => i.ok);

  return (
    <div className="lg:max-w-3xl space-y-3">
      <div className={cn(
        "flex items-start gap-3 rounded-xl border p-4",
        allGood ? "border-emerald-500/30 bg-emerald-500/5" : "border-amber-500/30 bg-amber-500/5",
      )}>
        <div className={cn(
          "flex h-9 w-9 items-center justify-center rounded-lg shrink-0",
          allGood ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300",
        )}>
          <Plug className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-medium text-zinc-100">
            {allGood ? "Everything looks good" : "Something needs attention"}
          </p>
          <p className="mt-0.5 text-xs text-zinc-400">
            {allGood
              ? "Your CRM is fully set up and ready to go."
              : "Below are the things your CRM needs in order to work. If anything says \"Needs setup\", ask your AI to fix it. You don't have to figure out the technical side."}
          </p>
        </div>
      </div>

      {/* Divider + section heading so the summary card above doesn't blur
          into the per-capability list below. */}
      <div className="flex items-center gap-3 pt-2">
        <span className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
          What your CRM needs
        </span>
        <div className="h-px flex-1 bg-zinc-800/70" />
      </div>

      {items.map((it) => <CapabilityRow key={it.title} {...it} />)}
    </div>
  );
}

function CapabilityRow({
  title, explainer, ok, Icon, details,
}: {
  title: string;
  explainer: string;
  ok: boolean;
  Icon: React.ComponentType<{ className?: string }>;
  details?: string;
}) {
  return (
    <div className="flex items-start gap-3 rounded-xl border border-zinc-800/70 bg-zinc-900/30 p-4">
      <div className={cn(
        "flex h-9 w-9 items-center justify-center rounded-lg shrink-0",
        ok ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300",
      )}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-medium text-zinc-100">{title}</p>
          <Badge color={ok ? "emerald" : "amber"} size="xs">{ok ? "Working" : "Needs setup"}</Badge>
        </div>
        <p className="mt-0.5 text-xs text-zinc-400">{explainer}</p>
        {details && (
          <p className="mt-1 text-xs text-amber-300">{details}</p>
        )}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-zinc-800/70 bg-zinc-900/30 p-5">
      <h3 className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-zinc-500">{title}</h3>
      {children}
    </div>
  );
}
